import {useEffect, useMemo, useState} from 'react';

/**
 * Постраничная разбивка для списка, который сервер отдаёт ЦЕЛИКОМ.
 *
 * Серверная разбивка лучше, и там, где маршрут её принимает (`page`/`per_page`),
 * страницу листает запрос, а не этот хук. Но часть маршрутов страницы не имеет вовсе
 * (`GET /control/desktops` — голый массив), а часть считает один неделимый ответ
 * (`GET /control/attendance` — это отчёт, а не страница строк). Для них выбор стоит не
 * между «клиентской и серверной разбивкой», а между «разбивкой и таблицей, которая
 * растёт без края».
 *
 * Поэтому хук называет вещи своими именами в месте вызова: кто читает экран, видит, что
 * страницу листает клиент, и не считает, что за стрелкой стоит запрос.
 *
 * Номер страницы зажимается, когда список под ним усох: отфильтровать три страницы до
 * одной и оставить читателя смотреть на третью — это пустой экран без объяснения.
 *
 * Номер может храниться снаружи (`controlled`) — обычно в адресе страницы, чтобы
 * ссылка на седьмую страницу открывалась седьмой страницей. Без этого он живёт внутри
 * хука, как и раньше.
 */
export function useClientPage<T>(
  items: readonly T[],
  perPage = 20,
  controlled?: {value: number; onChange: (next: number) => void},
) {
  const [own, setOwn] = useState(1);
  const page = controlled ? controlled.value : own;
  const total = items.length;
  const totalPages = Math.max(1, Math.ceil(total / perPage));
  const current = Math.min(page, totalPages);

  const setPage = controlled ? controlled.onChange : setOwn;

  useEffect(() => {
    if (page > totalPages) {
      setPage(totalPages);
    }
    // `setPage` стабилен по построению у обеих веток.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, totalPages]);

  const rows = useMemo(
    () => items.slice((current - 1) * perPage, current * perPage),
    [items, current, perPage],
  );

  return {rows, page: current, totalPages, total, perPage, setPage};
}
